/**
 * IntakesPage — the Intakes tab. Lists Oscar-sourced pending_approval projects in a table
 * (Projects-page style) with search, sort and a status filter. Leadership/admin can open the
 * full-page review wizard (/intakes/{jiraKey}/review) from a row or the Review button, or reject
 * via the modal. On mobile the table collapses to cards. Non-leadership users are redirected to
 * /projects (intakes are an approval surface).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, Inbox, RefreshCw, Search, X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useIntakes } from "@/hooks/useIntakes";
import type { OscarIntake } from "@/types/intake";
import {
  intakeArrivedAt,
  intakeArrivedMs,
  intakeAssigneeName,
  intakeCompany,
  intakeDealName,
  isNewIntake,
  formatExactDateTime,
  hoursSince,
  opportunityTypeBadgeClass,
  timeAgo,
} from "@/types/intake";
import { cn } from "@/lib/utils";
import { sortOptionsByLabel, sortTextOptions } from "@/lib/filter-options";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { IntakeCard } from "@/components/intakes/IntakeCard";
import { RejectIntakeModal } from "@/components/intakes/RejectIntakeModal";

type SortKey = "newest" | "oldest" | "company";
type StatusFilter = "all" | "pending" | "week";

/**
 * Toolbar copy. Inline strings are the established pattern on this page (this frontend has no i18n
 * runtime); they are hoisted here so each control's visible label and its accessible name cannot
 * drift apart.
 */
const TOOLBAR_LABELS = {
  sort: "Sort by",
  status: "Status",
  opportunityType: "Type",
  reject: "Reject",
} as const;

/** Sort dropdown. Ordering is semantic (recency first), not alphabetical — it is not a filter. */
const SORT_OPTIONS: ReadonlyArray<{ value: SortKey; label: string }> = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "company", label: "Company Name" },
];

/**
 * Status filter options — "All" pinned first, the rest A–Z (INT-08). Ordered in code so the order
 * cannot drift as values are added.
 */
const STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All" },
  ...sortOptionsByLabel<{ value: StatusFilter; label: string }>([
    { value: "pending", label: "Pending" },
    { value: "week", label: "This Week" },
  ]),
];

/**
 * Split an Oscar deal name into client (before the first " - ") and project (after).
 * Returns the whole string as the client with a null project when there's no hyphen.
 */
function parseDealName(raw: string): { client: string; project: string | null } {
  const idx = raw.indexOf(" - ");
  if (idx === -1) return { client: raw, project: null };
  return { client: raw.slice(0, idx).trim(), project: raw.slice(idx + 3).trim() };
}

/** A green NEW pill for intakes received in the last 48h. */
function NewBadge(): JSX.Element {  return (
    <Badge
      variant="success"
      className="px-1.5 py-0 text-[10px] uppercase tracking-wide"
    >
      New
    </Badge>
  );
}

/** The Deal cell's opportunity-type badge (colour-coded by type). */
function OpportunityBadge({
  type,
}: {
  type: string | null | undefined;
}): JSX.Element | null {
  if (!type) return null;
  return (
    <Badge variant="neutral" className={cn(opportunityTypeBadgeClass(type), "w-fit")}>
      {type}
    </Badge>
  );
}

/**
 * A toolbar dropdown with a persistent visible label beside it, so the field being filtered/sorted
 * is always identifiable from the trigger alone (a bare "All" says nothing about what it filters).
 * The label doubles as the control's accessible name.
 */
function LabeledSelect({
  label,
  value,
  onChange,
  options,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span className="whitespace-nowrap text-sm text-text-secondary">{label}</span>
      <Select
        value={value}
        onChange={onChange}
        className={cn("h-[42px]", className)}
        aria-label={label}
        options={options}
      />
    </div>
  );
}

function IntakesPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = user?.role === "admin" || user?.role === "leadership";

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [oppTypeFilter, setOppTypeFilter] = useState<string>("all");
  const [rejectTarget, setRejectTarget] = useState<OscarIntake | null>(null);

  const { data, isFetching, isLoading, isError, error, refetch } = useIntakes();

  // Distinct opportunity types present in the current intake set — drives the filter dropdown.
  // Ordered A–Z case-insensitively; "All Types" is pinned first at the call site (INT-08).
  const opportunityTypeOptions = useMemo<string[]>(() => {
    const set = new Set<string>();
    (data?.projects ?? []).forEach((i) => {
      if (i.opportunity_type) set.add(i.opportunity_type);
    });
    return sortTextOptions(Array.from(set));
  }, [data]);

  // Intakes are an approval surface — non-leadership users don't belong here.
  useEffect(() => {
    if (user && !canApprove) navigate("/projects", { replace: true });
  }, [user, canApprove, navigate]);

  const intakes = useMemo<OscarIntake[]>(() => {
    const list = data?.projects ?? [];
    const q = search.trim().toLowerCase();

    const filtered = list.filter((intake) => {
      // Status filter.
      if (
        statusFilter === "week" &&
        hoursSince(intakeArrivedAt(intake)) >= 24 * 7
      )
        return false;
      // 'pending' and 'all' both surface every intake (all are pending_approval); kept per spec.

      // Opportunity-type filter.
      if (oppTypeFilter !== "all" && intake.opportunity_type !== oppTypeFilter)
        return false;

      if (!q) return true;
      const haystack = [
        intakeCompany(intake),
        intakeDealName(intake),
        intake.company_name ?? "",
        intake.deal_name ?? "",
        intake.dealname ?? "",
        intake.jira_key ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sort) {
        case "oldest":
          return intakeArrivedMs(a) - intakeArrivedMs(b);
        case "company":
          return intakeCompany(a).localeCompare(intakeCompany(b));
        case "newest":
        default:
          return intakeArrivedMs(b) - intakeArrivedMs(a);
      }
    });
    return sorted;
  }, [data, search, sort, statusFilter, oppTypeFilter]);

  const openReview = (intake: OscarIntake): void => {
    navigate(`/intakes/${intake.jira_key}/review`);
  };

  if (!canApprove) return <></>;

  const totalCount = data?.projects.length ?? 0;
  const hasFilters =
    Boolean(search.trim()) || statusFilter !== "all" || oppTypeFilter !== "all";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-neutral-900">
              Intakes
            </h1>
            <Badge variant="warning">{totalCount}</Badge>
          </div>
          <p className="text-sm text-text-secondary">
            Pending deals from Oscar awaiting approval
          </p>
        </div>
        <Button
          variant="outline"
          className="h-12 gap-2"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </div>

      {/* Toolbar: search + sort + filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, deal or Jira key…"
            aria-label="Search intakes"
            className="h-[42px] pl-9 pr-9 bg-white border-outlines"
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-text-secondary"
            >
              <X />
            </Button>
          )}
        </div>

        <LabeledSelect
          label={TOOLBAR_LABELS.sort}
          value={sort}
          onChange={(v) => setSort(v as SortKey)}
          className="w-[150px]"
          options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />

        <LabeledSelect
          label={TOOLBAR_LABELS.status}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
          className="w-[130px]"
          options={STATUS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />

        <LabeledSelect
          label={TOOLBAR_LABELS.opportunityType}
          value={oppTypeFilter}
          onChange={setOppTypeFilter}
          className="w-[170px]"
          options={[
            { value: "all", label: "All Types" },
            ...opportunityTypeOptions.map((t) => ({ value: t, label: t })),
          ]}
        />
      </div>

      {/* Error */}
      {isError && !isLoading && (
        <Alert variant="destructive">
          <AlertTitle>Failed to load intakes</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {error instanceof Error
                ? error.message
                : "An unexpected error occurred."}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => refetch()}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
              />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Card>
          <CardContent className="space-y-3 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty */}
      {!isLoading && !isError && intakes.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Inbox className="h-7 w-7" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-text-primary">
                {hasFilters
                  ? "No matching intakes"
                  : "No pending intakes from Oscar"}
              </h3>
              <p className="max-w-sm text-sm text-text-secondary">
                {hasFilters
                  ? "Try adjusting your search or filters."
                  : "New deals pushed from Oscar will appear here for review and approval."}
              </p>
            </div>
            {hasFilters && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                  setOppTypeFilter("all");
                }}
              >
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Content — cards on mobile/tablet, table on wide screens */}
      {!isLoading && !isError && intakes.length > 0 && (
        <>
          {/* Card grid: xs–md (< 1024px) */}
          <div className="grid grid-cols-1 gap-4 lg:hidden">
            {intakes.map((intake) => (
              <IntakeCard
                key={intake.id}
                intake={intake}
                canApprove={canApprove}
                onReview={openReview}
                onReject={setRejectTarget}
              />
            ))}
          </div>

          {/* Table: lg+ — sticky header, body scrolls internally */}
          <div className="hidden lg:block">
            <TooltipProvider delayDuration={200}>
              <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                {/* Constrain horizontal overflow to the card only — never the page */}
                <div className="w-full overflow-x-auto">
                  <Table className="min-w-[640px]">
                    <TableHeader className="sticky top-0 z-10 bg-neutral-50">
                      <TableRow>
                        <TableHead>Deal</TableHead>
                        <TableHead className="w-[180px]">Opportunity Type</TableHead>
                        <TableHead className="hidden w-32 xl:table-cell">SOW Hours</TableHead>
                        <TableHead className="hidden 2xl:table-cell">Assigned SA</TableHead>
                        <TableHead className="w-36">Received</TableHead>
                        <TableHead className="w-32 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {intakes.map((intake) => {
                        return (
                          <TableRow
                            key={intake.id}
                            onClick={() => openReview(intake)}
                            className="cursor-pointer"
                          >
                            {/* Deal — always */}
                            <TableCell>
                              <div className="flex min-w-0 flex-col gap-1">
                                {(() => {
                                  const { client, project } = parseDealName(
                                    intakeDealName(intake),
                                  );
                                  return (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <span className="truncate font-medium text-text-primary">
                                          {client}
                                        </span>
                                        {isNewIntake(intake) && <NewBadge />}
                                      </div>
                                      {project && (
                                        <span className="truncate text-xs text-text-secondary">
                                          {project}
                                        </span>
                                      )}
                                      {/* Jira key — the search box matches on it, so it has to be
                                          visible for a user to confirm the right row (INT-03). */}
                                      {intake.jira_key && (
                                        <span className="truncate font-mono text-xs text-text-secondary">
                                          {intake.jira_key}
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </TableCell>

                            {/* Opportunity Type — always */}
                            <TableCell>
                              {intake.opportunity_type ? (
                                <OpportunityBadge type={intake.opportunity_type} />
                              ) : (
                                <span className="text-sm text-text-secondary">—</span>
                              )}
                            </TableCell>

                            {/* SOW Hours — xl+ */}
                            <TableCell className={cn(
                              "hidden xl:table-cell",
                              intake.sow_hours != null ? "font-medium text-text-primary" : "text-text-secondary"
                            )}>
                              {intake.sow_hours ?? "—"}
                            </TableCell>

                            {/* Assigned SA — 2xl+ */}
                            <TableCell className="hidden truncate text-text-secondary 2xl:table-cell">
                              {intakeAssigneeName(intake) ?? "—"}
                            </TableCell>

                            {/* Received — always. Relative time for scanning, exact
                                date+time behind a hover/focus tooltip (INT-05). */}
                            <TableCell className="whitespace-nowrap text-sm text-text-secondary">
                              {(() => {
                                const arrivedAt = intakeArrivedAt(intake);
                                const exact = formatExactDateTime(arrivedAt);
                                return (
                                  <Tooltip text={exact}>
                                    <span
                                      // Only a focus stop when there is an exact date to reveal.
                                      tabIndex={exact ? 0 : undefined}
                                      title={exact ?? undefined}
                                      className="rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                                    >
                                      {timeAgo(arrivedAt)}
                                    </span>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>

                            {/* Actions — always */}
                            <TableCell className="text-right">
                              <div
                                className="flex items-center justify-end gap-1.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button size="sm" onClick={() => openReview(intake)}>
                                  Review
                                </Button>
                                {/* Icon-only — carries both a tooltip and an accessible name.
                                    Opens RejectIntakeModal, which requires a written reason, so the
                                    consequential step stays confirmed (INT-02). */}
                                <Tooltip text={TOOLBAR_LABELS.reject}>
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-8 w-8 text-text-secondary hover:text-error"
                                    aria-label={`${TOOLBAR_LABELS.reject} ${intakeCompany(intake)}`}
                                    onClick={() => setRejectTarget(intake)}
                                  >
                                    <Ban className="h-4 w-4" aria-hidden="true" />
                                  </Button>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TooltipProvider>
          </div>
        </>
      )}

      {/* Reject modal */}
      <RejectIntakeModal
        open={rejectTarget !== null}
        onOpenChange={(o) => !o && setRejectTarget(null)}
        intake={rejectTarget}
      />
    </div>
  );
}

export default IntakesPage;
